import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { PageBody, PageHeader } from '@/components/AppShell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Dialog, DialogBody, DialogFooter } from '@/components/ui/dialog';
import { chartApi, type ChartOfAccount, type ChartTreeNode, type ChartUsage } from '@/api';
import { ApiError } from '@/lib/api';

type Nature = 'REVENUE' | 'EXPENSE';

interface FormState {
  name: string;
  nature: Nature;
}

const emptyForm: FormState = { name: '', nature: 'EXPENSE' };

export function PlanoContasPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['chart-tree'], queryFn: chartApi.tree });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ChartOfAccount | null>(null);
  const [deleting, setDeleting] = useState<ChartOfAccount | null>(null);

  function openNew() {
    setEditing(null);
    setFormOpen(true);
  }
  function openEdit(node: ChartOfAccount) {
    setEditing(node);
    setFormOpen(true);
  }

  return (
    <>
      <PageHeader
        title="Plano de contas"
        breadcrumb="Cadastros"
        actions={
          <Button size="sm" onClick={openNew}>
            <Plus className="w-4 h-4" /> Nova subcategoria
          </Button>
        }
      />
      <PageBody>
        <div className="bg-card border rounded-lg p-4">
          {isLoading && <div className="text-muted-foreground">Carregando…</div>}
          {data?.map((node) => (
            <Node key={node.id} node={node} depth={0} onEdit={openEdit} onDelete={setDeleting} />
          ))}
          {!isLoading && (data?.length ?? 0) === 0 && (
            <div className="text-center text-muted-foreground py-8">Nenhuma categoria cadastrada</div>
          )}
        </div>

        {formOpen && (
          <FormDialog
            tree={data ?? []}
            editing={editing}
            onClose={() => setFormOpen(false)}
            onSaved={() => {
              setFormOpen(false);
              qc.invalidateQueries({ queryKey: ['chart-tree'] });
            }}
          />
        )}

        {deleting && (
          <DeleteDialog
            target={deleting}
            onClose={() => setDeleting(null)}
            onDeleted={() => {
              setDeleting(null);
              qc.invalidateQueries({ queryKey: ['chart-tree'] });
            }}
          />
        )}
      </PageBody>
    </>
  );
}

function Node({
  node,
  depth,
  onEdit,
  onDelete,
}: {
  node: ChartTreeNode;
  depth: number;
  onEdit: (n: ChartOfAccount) => void;
  onDelete: (n: ChartOfAccount) => void;
}) {
  const editable = !node.isSystem;
  return (
    <div className="border-l border-muted pl-4 ml-2">
      <div className="flex items-center gap-2 py-1.5 group">
        <span className="font-mono text-xs text-muted-foreground">{node.code}</span>
        <span>{node.name}</span>
        {node.nature === 'REVENUE' && <Badge variant="success">Receita</Badge>}
        {node.nature === 'EXPENSE' && <Badge variant="warning">Despesa</Badge>}
        {!node.active && <Badge>Inativo</Badge>}
        {node.isSystem && <Badge>Sistema</Badge>}
        {editable && (
          <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => onEdit(node)}
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
              title="Editar"
              aria-label="Editar"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onDelete(node)}
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive"
              title="Excluir"
              aria-label="Excluir"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      {node.children.map((c) => (
        <Node key={c.id} node={c} depth={depth + 1} onEdit={onEdit} onDelete={onDelete} />
      ))}
    </div>
  );
}

// ===================== Form (criar / editar) =====================

function FormDialog({
  tree,
  editing,
  onClose,
  onSaved,
}: {
  tree: ChartTreeNode[];
  editing: ChartOfAccount | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!editing;
  const [form, setForm] = useState<FormState>(
    editing
      ? { name: editing.name, nature: editing.nature === 'REVENUE' ? 'REVENUE' : 'EXPENSE' }
      : emptyForm,
  );

  // Roots por natureza, vindo da própria árvore.
  const revenueRoot = tree.find((n) => n.nature === 'REVENUE' && n.parentId === null);
  const expenseRoot = tree.find((n) => n.nature === 'EXPENSE' && n.parentId === null);

  /** Próximo código sequencial sob o root: parentCode + ".NN" */
  function nextCodeFor(parent: ChartTreeNode): string {
    const re = new RegExp(`^${parent.code}\\.(\\d+)$`);
    const used = parent.children
      .map((c) => {
        const m = re.exec(c.code);
        return m ? Number(m[1]) : 0;
      })
      .filter((n) => n > 0);
    const next = (used.length ? Math.max(...used) : 0) + 1;
    return `${parent.code}.${String(next).padStart(2, '0')}`;
  }

  const create = useMutation({
    mutationFn: (body: { code: string; name: string; nature: Nature; parentId: string }) =>
      chartApi.create(body),
    onSuccess: onSaved,
  });
  const update = useMutation({
    mutationFn: (body: { name: string }) =>
      chartApi.update(editing!.id, body),
    onSuccess: onSaved,
  });

  const mut = isEdit ? update : create;
  const apiError = mut.error instanceof ApiError ? mut.error.message : null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) return;
    if (isEdit) {
      update.mutate({ name });
      return;
    }
    const root = form.nature === 'REVENUE' ? revenueRoot : expenseRoot;
    if (!root) {
      alert('Categoria raiz (RECEITAS/DESPESAS) não encontrada. Execute o seed.');
      return;
    }
    create.mutate({
      code: nextCodeFor(root),
      name,
      nature: form.nature,
      parentId: root.id,
    });
  }

  return (
    <Dialog open onClose={onClose} title={isEdit ? 'Editar subcategoria' : 'Nova subcategoria'}>
      <form onSubmit={handleSubmit}>
        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="chart-name">Nome</Label>
            <Input
              id="chart-name"
              autoFocus
              maxLength={120}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Ex.: Hospedagem, VPS, Material de escritório…"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="chart-nature">Tipo</Label>
            <Select
              id="chart-nature"
              value={form.nature}
              disabled={isEdit}
              onChange={(e) => setForm((f) => ({ ...f, nature: e.target.value as Nature }))}
            >
              <option value="EXPENSE">Despesa (sob DESPESAS)</option>
              <option value="REVENUE">Receita (sob RECEITAS)</option>
            </Select>
            {isEdit && (
              <p className="text-xs text-muted-foreground">
                O tipo (Receita/Despesa) não pode ser alterado depois de criado.
              </p>
            )}
          </div>
          {apiError && <p className="text-sm text-destructive">{apiError}</p>}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={mut.isPending || !form.name.trim()}>
            {mut.isPending ? 'Salvando…' : isEdit ? 'Salvar' : 'Criar'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

// ===================== Excluir (com reassign) =====================

function DeleteDialog({
  target,
  onClose,
  onDeleted,
}: {
  target: ChartOfAccount;
  onClose: () => void;
  onDeleted: () => void;
}) {
  // Carrega contagem de uso assim que o modal abre.
  const { data: usage, isLoading: loadingUsage } = useQuery<ChartUsage>({
    queryKey: ['chart-usage', target.id],
    queryFn: () => chartApi.usage(target.id),
  });

  const remove = useMutation({
    mutationFn: (reassign: boolean) => chartApi.remove(target.id, reassign),
    onSuccess: onDeleted,
  });
  const apiError = remove.error instanceof ApiError ? remove.error.message : null;

  const total = usage?.total ?? 0;
  const inUse = total > 0;

  return (
    <Dialog open onClose={onClose} title="Excluir subcategoria">
      <DialogBody className="space-y-4">
        <p>
          Excluir a subcategoria <strong>{target.name}</strong>{' '}
          <span className="font-mono text-xs text-muted-foreground">({target.code})</span>?
        </p>

        {loadingUsage && <p className="text-sm text-muted-foreground">Verificando uso…</p>}

        {!loadingUsage && !inUse && (
          <p className="text-sm text-muted-foreground">
            Nenhum lançamento usa esta subcategoria. A exclusão é segura.
          </p>
        )}

        {!loadingUsage && inUse && usage && (
          <div className="rounded-md border border-warning/40 bg-warning/5 p-3 text-sm space-y-2">
            <p className="font-medium">
              Esta subcategoria está em uso em {total} registro(s):
            </p>
            <ul className="list-disc list-inside text-muted-foreground">
              {usage.transactions > 0 && <li>{usage.transactions} lançamento(s) em Caixa</li>}
              {usage.payables > 0 && <li>{usage.payables} conta(s) a pagar</li>}
              {usage.receivables > 0 && <li>{usage.receivables} conta(s) a receber</li>}
              {usage.recurringRules > 0 && <li>{usage.recurringRules} regra(s) recorrente(s)</li>}
            </ul>
            <p>
              Ao confirmar, todos serão movidos para a subcategoria{' '}
              <strong>Outros</strong> ({target.nature === 'REVENUE' ? 'Receitas' : 'Despesas'}) e
              esta subcategoria será excluída.
            </p>
          </div>
        )}

        {apiError && <p className="text-sm text-destructive">{apiError}</p>}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button
          type="button"
          variant="destructive"
          disabled={loadingUsage || remove.isPending}
          onClick={() => remove.mutate(inUse)}
        >
          {remove.isPending
            ? 'Excluindo…'
            : inUse
              ? `Mover ${total} e excluir`
              : 'Excluir'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
